import React, { useEffect, useRef, useCallback, useImperativeHandle, forwardRef, useState } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { Mic, MicOff, Loader2 } from 'lucide-react';
import '@xterm/xterm/css/xterm.css';
import { useSpeechInput } from '../hooks/useSpeechInput';

interface SimpleTerminalProps {
  id: string;
  visible: boolean;
  onStatusChange?: (status: 'disconnected' | 'connecting' | 'connected') => void;
}

export interface SimpleTerminalHandle {
  sendInput: (text: string) => void;
  focus: () => void;
}

const SimpleTerminal = forwardRef<SimpleTerminalHandle, SimpleTerminalProps>(({ id, visible, onStatusChange }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const termRef = useRef<Terminal | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const initRef = useRef(false);
  const onStatusChangeRef = useRef(onStatusChange);
  onStatusChangeRef.current = onStatusChange;

  // Voice input state
  const [pendingVoiceText, setPendingVoiceText] = useState('');

  const handleVoiceFinal = useCallback((text: string) => {
    setPendingVoiceText(prev => prev + text);
  }, []);

  const {
    isListening,
    interimTranscript,
    isSupported: voiceSupported,
    start: startVoice,
    stop: stopVoice,
    getFullTranscript
  } = useSpeechInput({
    onFinalTranscript: handleVoiceFinal,
    continuous: true
  });

  const handleVoiceToggle = useCallback(() => {
    if (isListening) {
      stopVoice();
      const fullText = getFullTranscript();
      if (fullText.trim() && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'input', data: fullText.trim() + '\n' }));
        termRef.current?.focus();
      }
      setPendingVoiceText('');
    } else {
      setPendingVoiceText('');
      startVoice();
    }
  }, [isListening, startVoice, stopVoice, getFullTranscript]);

  // Expose methods via ref for parent components (voice input injection)
  useImperativeHandle(ref, () => ({
    sendInput: (text: string) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'input', data: text }));
      }
    },
    focus: () => {
      termRef.current?.focus();
    }
  }), []);

  // Initialize function - called once when first visible
  const initialize = useCallback(() => {
    if (initRef.current || !containerRef.current) return;
    initRef.current = true;

    const term = new Terminal({
      cols: 120,
      rows: 30,
      theme: {
        background: 'transparent',
        foreground: '#e5e7eb',
        cursor: '#06b6d4',
        cursorAccent: '#06b6d4',
        selectionBackground: '#06b6d450',
        selectionForeground: '#ffffff',
      },
      fontFamily: 'JetBrains Mono, Menlo, Monaco, "Courier New", monospace',
      fontSize: 13,
      cursorBlink: true,
      allowTransparency: true,
    });

    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(containerRef.current);

    setTimeout(() => fit.fit(), 100);

    termRef.current = term;
    fitRef.current = fit;

    // WebSocket
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.protocol === 'https:' ? window.location.host : 'localhost:3102';
    const url = `${proto}//${host}/ws/claude-terminal?terminal=${id}`;

    onStatusChangeRef.current?.('connecting');
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      onStatusChangeRef.current?.('connected');
      // Send initial size immediately on connect
      fit.fit();
      if (term.cols >= 20 && term.rows >= 5) {
        ws.send(JSON.stringify({ type: 'resize', cols: term.cols, rows: term.rows }));
      }

      term.onData(data => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'input', data }));
        }
      });
    };

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === 'output') {
          term.write(msg.data);
        } else if (msg.type === 'connected') {
          term.writeln(`\r\n\x1b[32m✓ Connected to ${msg.shell}\x1b[0m\r\n`);
        } else if (msg.type === 'exit') {
          term.writeln(`\r\n\x1b[33mSession ended (code: ${msg.code})\x1b[0m`);
        }
      } catch {
        term.write(e.data);
      }
    };

    ws.onclose = () => onStatusChangeRef.current?.('disconnected');
    ws.onerror = () => onStatusChangeRef.current?.('disconnected');

    const handleResize = () => {
      fit.fit();
      if (ws.readyState === WebSocket.OPEN && term.cols >= 20 && term.rows >= 5) {
        ws.send(JSON.stringify({ type: 'resize', cols: term.cols, rows: term.rows }));
      }
    };
    window.addEventListener('resize', handleResize);

    // Also fit on visibility/layout changes
    const resizeObserver = new ResizeObserver(() => {
      fit.fit();
      if (ws.readyState === WebSocket.OPEN && term.cols >= 20 && term.rows >= 5) {
        ws.send(JSON.stringify({ type: 'resize', cols: term.cols, rows: term.rows }));
      }
    });
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Store cleanup for unmount
    return () => {
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
      ws.close();
      term.dispose();
    };
  }, [id]);

  // Trigger initialization when first visible
  useEffect(() => {
    if (visible && !initRef.current) {
      initialize();
    }
  }, [visible, initialize]);

  // Cleanup on unmount ONLY
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (termRef.current) {
        termRef.current.dispose();
      }
    };
  }, []);

  // Fit when becoming visible (after init) and sync with backend
  useEffect(() => {
    if (visible && fitRef.current && initRef.current && termRef.current) {
      setTimeout(() => {
        fitRef.current?.fit();
        // Also send resize to backend
        if (wsRef.current?.readyState === WebSocket.OPEN && termRef.current && termRef.current.cols >= 20 && termRef.current.rows >= 5) {
          wsRef.current.send(JSON.stringify({
            type: 'resize',
            cols: termRef.current.cols,
            rows: termRef.current.rows
          }));
        }
      }, 50);
    }
  }, [visible]);

  return (
    <div className={`relative h-full w-full ${visible ? '' : 'hidden'}`} style={{ minHeight: '200px' }}>
      {/* Terminal container */}
      <div ref={containerRef} className="h-full w-full" />

      {/* Floating voice input button */}
      {voiceSupported && (
        <div className="absolute bottom-3 right-3 z-10">
          <button
            onClick={handleVoiceToggle}
            className={`relative p-3 rounded-full transition-all duration-200 shadow-lg ${
              isListening
                ? 'bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-cyan-500/40'
                : 'bg-gray-900/90 text-gray-400 hover:bg-gray-800 hover:text-white border border-gray-700/50'
            }`}
            title={isListening ? 'Stop & send to terminal' : 'Voice input'}
          >
            {isListening ? (
              <>
                <MicOff className="w-5 h-5" />
                <span className="absolute inset-0 rounded-full border-2 border-cyan-400 animate-ping opacity-50" />
              </>
            ) : (
              <Mic className="w-5 h-5" />
            )}
          </button>

          {/* Live transcript popup */}
          {isListening && (pendingVoiceText || interimTranscript) && (
            <div className="absolute bottom-full right-0 mb-2 w-64">
              <div className="bg-gray-900/95 border border-cyan-500/30 rounded-lg px-3 py-2 shadow-lg">
                <div className="text-[10px] text-cyan-400 mb-1 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Listening... (click mic to send)
                </div>
                <p className="text-xs text-gray-300 whitespace-pre-wrap max-h-24 overflow-y-auto">
                  {pendingVoiceText}
                  <span className="text-cyan-400/70">{interimTranscript}</span>
                  <span className="animate-pulse">|</span>
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

SimpleTerminal.displayName = 'SimpleTerminal';

export default SimpleTerminal;
